import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from 'axios';
import { setLoading } from "./LoadingSlice";
import { toast } from "react-toastify";
import Unauthorized from '../Admin/Unauthorized';
import BaseUrl from "../../BaseUrl";

const headers = Unauthorized.Actionsauthorized();

const authurl = BaseUrl.authurl
const consultationurl =BaseUrl.consultationurl
const invoiceurl = BaseUrl.invoiceurl
const admissionurl = BaseUrl.hmsurl


// Utility function to get the initial patient-related state
const getInitialPatientState = () => ({
    contactDetails: {
        name: '',
        phone: '',
        email: '',
        address: '',
        countrycode: '',
        addressDetails: { 
            Street1: '',
            Street2: '',
            City: '',
            State: '',
            PostalCode: '',
            Country: '',
        },
    },
    patientDetails: {
        dob: '',
        age: '',
        gender: '',
        height: '',
        weight: '',
        bloodtype: '',
        allergies: []
    },
    insuranceDetails: {
        InsuranceProvider: "",
        PolicyNumber: "",
        IssueDate: "",
        InsuranceExpiry: ""
    },
    patientMRN: null,
});


const initialState = {
    ...getInitialPatientState(), // Use the utility function here
    departments: [],
    consultationtype: 'out_patient',
    selectedDepartment: null,
    doctors: [],
    selectedDoctor: null,
    consultationDate: null,
    consultationTime: null,
    admissionType: null,
    admissionDate: null,
    selectedFloorId: null,
    selectedRoomId: null,
    selectedBedId: null,
    selectedRoomCategory: null, 
    paymentMode: null,
    addedInsurance: [],
    patients: [],
    paymentModes: [],
    admissionReason: null,
};


export const fetchDepartments = () => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const response = await axios.get(authurl + 'Departments/GetAllDepartments',{headers});
        dispatch(setDepartments(response.data));
    } catch (error) {
        console.error("Error fetching departments:", error);
    } finally {
        dispatch(setLoading(false));
    }
};

export const getdocbydeptid = (id) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const response = await axios.get(`${authurl}Auth/GetDoctorsByDepartmentId?DepartmentId=${id}`);
        dispatch(setDoctors(response.data));
    } catch (error) {
        console.error("Error fetching department by ID:", error);
    } finally {
        dispatch(setLoading(false));
    }
};

export const GetPatientsbySearch = (searchTerm) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const response = await axios.get(`${authurl}Auth/GetPatients?id=${searchTerm}`);
        dispatch(setPatients(response.data));
    } catch (error) {
        console.error("Error fetching autocomplete results:", error);
    } finally {
        dispatch(setLoading(false));
    }
};

export const addPatient = (patientData) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        
        const response = await axios.post(`${authurl}Auth/AddPatient`, patientData);
        
        dispatch(setPatientMRN(response.data.Id));
    } catch (error) {
        console.error("Error adding patient:", error);
    } finally {
        dispatch(setLoading(false));
    }
};

export const addConsultation = createAsyncThunk(
    'adminbooking/addConsultation',
    async (consultationData, { dispatch }) => {
        try {
            dispatch(setLoading(true));
            const response = await axios.post(`${consultationurl}Consultations/AddNewConsultation`, consultationData);
            toast.success('Consultation booked successfully!');
            dispatch(resetConsultation()); 
            return response.data;
        } catch (error) {
            console.error("Error adding consultation:", error);
            toast.error("Error adding consultation:");
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const addAdmission = createAsyncThunk(
    'adminbooking/addAdmission',
    async (admissionData, { dispatch }) => {
        try {
            dispatch(setLoading(true));
            const response = await axios.post(`${admissionurl}Admissions`, admissionData);
            toast.success('Admission booked successfully!');
            
            dispatch(resetConsultation()); 
            return response.data;
        } catch (error) {
            console.error("Error adding admission:", error);
            toast.error("Error adding admission:");
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const fetchPatientByMRN = createAsyncThunk(
    'adminbooking/fetchPatientByMRN',
    async (mrn, { dispatch }) => {
        try {
            dispatch(setLoading(true));
            const response = await axios.get(`${authurl}Patients/${mrn}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching patient by MRN:", error);
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    }
);



export const addInsuranceAPI = createAsyncThunk(
    'adminbooking/addInsuranceAPI',
    async ({ mrn, insuranceData }, { dispatch }) => {
        try {
            dispatch(setLoading(true));
            const response = await axios.post(`${authurl}Auth/AddInsurance?mrn=${mrn}`, insuranceData);
            return {
                InsuranceProvider: insuranceData.InsuranceProvider,
                PolicyNumber: insuranceData.PolicyNumber,
                IssueDate: insuranceData.IssueDate,
                InsuranceExpiry: insuranceData.InsuranceExpiry
            };
        } catch (error) {
            console.error("Error adding insurance:", error);
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const fetchPaymentModes = createAsyncThunk(
    'adminbooking/fetchPaymentModes',
    async (_, { dispatch }) => {
        try {
            dispatch(setLoading(true));
            const response = await axios.get(`${invoiceurl}PaymentTypeMasters`);
            return response.data;
        } catch (error) {
            console.error("Error fetching payment modes:", error);
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    }
);


const AdminBookingSlice = createSlice({
    name: "adminbooking",
    initialState,
    reducers: {
        setContactDetails: (state, action) => {
            state.contactDetails = action.payload;
        },
        setPatientDetails: (state, action) => {
            state.patientDetails = action.payload;
        },
        setInsuranceDetails: (state, action) => {
            state.insuranceDetails = action.payload;
        },
        addInsurance: (state, action) => {
            state.addedInsurance.push(action.payload);
        },
        setDepartments: (state, action) => {
            state.departments = action.payload;
        },
        setSelectedDepartment: (state, action) => {
            state.selectedDepartment = action.payload;
        },
        setDoctors: (state, action) => {
            state.doctors = action.payload;
        },
        setSelectedDoctor: (state, action) => {
            state.selectedDoctor = action.payload;
        },
        setConsultationDate: (state, action) => {
            state.consultationDate = action.payload;
        },
        setConsultationTime: (state, action) => {
            state.consultationTime = action.payload;
        },
        setPaymentMode: (state, action) => {
            state.paymentMode = action.payload;
        },
        resetConsultation: (state) => {
            return initialState;
        },
        // NEW REDUCER: Resets only the patient creation step details
        resetPatientStepDetails: (state) => {
            const newPatientState = getInitialPatientState();
            state.contactDetails = newPatientState.contactDetails;
            state.patientDetails = newPatientState.patientDetails;
            state.insuranceDetails = newPatientState.insuranceDetails;
            state.patientMRN = newPatientState.patientMRN;
        },
        setPatients: (state, action) => {
            state.patients = action.payload;
        },
        setMRN: (state, action) => {
            state.mrn = action.payload;
        },
        setPatientMRN: (state, action) => {
            state.patientMRN = action.payload;
        },
        setConsultationType: (state, action) => {
            state.consultationtype = action.payload;
        },
        setAdmissionDetails: (state, action) => {
            const { type, date } = action.payload;
            state.admissionType = type;
            state.admissionDate = date;
        },
        setSelectedFloorId: (state, action) => {
            state.selectedFloorId = action.payload;
            state.selectedRoomId = null;
            state.selectedBedId = null;
        },
        setSelectedRoomId: (state, action) => {
            state.selectedRoomId = action.payload;
            state.selectedBedId = null;
        },
        setSelectedBedId: (state, action) => {
            state.selectedBedId = action.payload;
        },
        setSelectedRoomCategory: (state, action) => {
            state.selectedRoomCategory = action.payload;
        },
        setAdmissionReason: (state, action) => {
            state.admissionReason = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
           
            .addCase(fetchPatientByMRN.fulfilled, (state, action) => {
        const patientData = action.payload;
        if (patientData) {
            state.patientDetails = {
                ...state.patientDetails,
                dob: patientData.Dob || '',
                gender: patientData.Gender || '',
                height: patientData.Height || '',
                weight: patientData.Weight || '',
                bloodtype: patientData.BloodType || '',
                allergies: patientData.Allergies || []
            };
            // Store insurance profiles properly
            state.addedInsurance = patientData.InsuranceProfiles || [];
            
            // Set first insurance as selected for backward compatibility
            if (state.addedInsurance.length > 0) {
                const firstInsurance = state.addedInsurance[0];
                state.insuranceDetails = {
                    InsuranceProvider: firstInsurance.InsuranceProvider || '',
                    PolicyNumber: firstInsurance.PolicyNumber || '',
                    IssueDate: firstInsurance.IssueDate || '',
                    InsuranceExpiry: firstInsurance.InsuranceExpiry || ''
                };
            }
        }
    })
            .addCase(addConsultation.pending, (state) => {
                state.loading = true;
            })
            .addCase(addConsultation.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(addConsultation.rejected, (state, action) => {
                state.loading = false;
            })
            .addCase(addAdmission.pending, (state) => {
                state.loading = true;
            })
            .addCase(addAdmission.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(addAdmission.rejected, (state, action) => {
                state.loading = false;
            })
            .addCase(fetchPaymentModes.fulfilled, (state, action) => {
                state.paymentModes = action.payload;
            })
            .addCase(fetchPaymentModes.rejected, (state, action) => {
                console.error("Failed to fetch payment modes:", action.error);
                state.paymentModes = [];
            })
             .addCase(addInsuranceAPI.pending, (state) => {
        state.loading = true;
    })
    .addCase(addInsuranceAPI.fulfilled, (state, action) => {
        state.loading = false;
        state.addedInsurance.push(action.payload);
    })
    .addCase(addInsuranceAPI.rejected, (state, action) => {
        state.loading = false;
        console.error("Failed to add insurance:", action.error);
    });

    }
});

export const {
    setContactDetails,
    setPatientDetails,
    setInsuranceDetails,
    addInsurance,
    setDepartments,
    setSelectedDepartment,
    setDoctors,
    setSelectedDoctor,
    setConsultationDate,
    setConsultationTime,
    setPaymentMode,
    resetConsultation,
    resetPatientStepDetails, // <-- Exported new action
    setPatients,
    setPatientMRN,
    setConsultationType,
    setAdmissionDetails,
    setSelectedFloorId,
    setSelectedRoomId,
    setSelectedBedId,
    setSelectedRoomCategory,
    setAdmissionReason
} = AdminBookingSlice.actions;

export default AdminBookingSlice.reducer;